/* eslint-disable prefer-destructuring */
import {
  EventService,
  AuthService,
} from '@pingum/api-services';
import { EventSourcePolyfill } from 'event-source-polyfill';
import moment from 'moment';
import { groupBy } from 'lodash';
import BarnService from '../../services/BarnService';
import TagReminderService from '../../services/TagReminderService';
import * as types from './types';
import { getAllPagedSearchSchedulesData } from '../schedule/actions';
import { generateEventsFromSchedules } from '../../utils/scheduleHelper';

let suggestionRequestId = 0;
let boardRequestId = 0;
let eventSource = null;
let reconnectAttempt = 0;
let currentSseTenant = '';
const isStringJson = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export function getTagConditions(barnHorses, location, taskTypeTags, filterTags) {
  let tagConditions = [];

  if (barnHorses.length > 0) {
    if (location && location.pingumTagId) {
      // include location and horses in condition
      tagConditions = [[parseInt(location.pingumTagId, 10), ...barnHorses.map((barnHorse) => parseInt(barnHorse.pingumTagId, 10))]];
    } else {
      // just include horses in condition
      tagConditions = [barnHorses.map((barnHorse) => parseInt(barnHorse.pingumTagId, 10))];
    }
  } else if (location && location.pingumTagId) {
    // just include location in condition
    tagConditions = [[parseInt(location.pingumTagId, 10)]];
  }

  // add task type tags if present
  if (taskTypeTags.length > 0) {
    tagConditions = [...tagConditions, taskTypeTags];
  }

  // add filter tags if present
  if (filterTags.length > 0) {
    tagConditions = [...tagConditions, filterTags];
  }

  return tagConditions;
}

/*
  Looped pagination helpers
*/

export const getAllPagedBarnHorses = async (barnId, page, pageSize, sort, search, lodgingDate, lodgingDateEnd, locationId, allContent = []) => {
  const response = await BarnService.getBarnHorses(barnId, page, pageSize, sort, search, lodgingDate, lodgingDateEnd, locationId);
  const newAllContent = [...allContent, ...response.content];

  if (response.last) { return newAllContent; }
  return getAllPagedBarnHorses(barnId, page + 1, pageSize, sort, search, lodgingDate, lodgingDateEnd, locationId, newAllContent);
};

export const getAllPagedTasks = async (searchEventsRequest, page, size, sortStartDateDesc, allContent = []) => {
  const response = await EventService.searchEvents(searchEventsRequest, page, size, sortStartDateDesc);
  const newAllContent = [...allContent, ...response.content];

  if (response.last || page >= 3) { return newAllContent; } // TODO for now we cap responses on tasks... what to do if nothing is being completed?
  return getAllPagedTasks(searchEventsRequest, page + 1, size, sortStartDateDesc, newAllContent);
};

/*
  BARN BOARD
*/

export const getBarnBoardSuggestions = (barnHorses) => (dispatch, getState) => {
  const {
    barn: { selectedBarn },
    location: { selectedLocation },
  } = getState();
  suggestionRequestId += 1;
  const requestId = suggestionRequestId;
  const reminderDueDateCutoff = moment().add(14, 'days').toISOString();
  dispatch({
    type: types.GET_BOARD_SUGGESTION_REQUESTED,
    requestId,
  });
  return Promise.all([
    BarnService.getHorseSuggestions(selectedBarn.id, selectedLocation && selectedLocation.id),
    TagReminderService.getTagRemindersForDueDate(
      barnHorses.map((bh) => parseInt(bh.pingumTagId, 10)),
      new Date(reminderDueDateCutoff),
    ),
  ]).then(([horseSuggestions, upcomingDueDates]) => {
    dispatch({
      type: types.GET_BOARD_SUGGESTION_SUCCESS,
      requestId,
      upcomingDueDates,
      horseSuggestions,
      reminderDueDateCutoff,
    });
  }).catch(() => {
    dispatch({
      type: types.GET_BOARD_SUGGESTION_ERROR,
      requestId,
    });
  });
};

export const getBarnBoard = (
  startDate,
  endDate,
  location,
  filteredBarnHorses, // provided if wanting a subset of location horses
  taskTypeTags,
  filterTags,
  {
    includeIncompleteTasks,
    forceBarnHorseRefresh,
    auth0UserId,
    scheduleAssignmentAuth0UserId,
    eventTemplateIds,
    background = false,
    catchUpAttempt = 0,
  },
) => (dispatch, getState) => {
  const searchKey = `${startDate.getTime()}-${endDate.getTime()}-${location.id}-${filteredBarnHorses.length}`; // Used to identify if we need to load new barn horses if dates or location change
  const {
    barn: {
      barnUsersMap,
      selectedBarn,
      allBarnHorses,
    },
    eventTemplate: { eventTemplates },
    board: {
      boardSearchKey,
      boardDataScopeKey,
      barnHorses,
      location: boardLocation,
    },
    status: { statuses },
  } = getState();
  const sortedIds = (values) => [...(values || [])].map(Number).sort((left, right) => left - right);
  const dataScopeKey = JSON.stringify([
    selectedBarn.id,
    selectedBarn.tenant,
    startDate.getTime(),
    endDate.getTime(),
    location.id,
    sortedIds(filteredBarnHorses.map((horse) => horse.pingumTagId)),
    sortedIds(taskTypeTags),
    sortedIds(filterTags),
    sortedIds(eventTemplateIds),
    auth0UserId,
    scheduleAssignmentAuth0UserId,
    !!includeIncompleteTasks,
  ]);
  const boardFilters = {
    eventTemplateIds,
    auth0UserId,
    scheduleAssignmentAuth0UserId,
    includeIncompleteTasks: !!includeIncompleteTasks,
  };
  boardRequestId += 1;
  const requestId = boardRequestId;
  const isCurrentRequest = () => getState().board.boardRequestId === requestId
    && getState().barn.selectedBarn?.id === selectedBarn.id
    && getState().barn.selectedBarn?.tenant === selectedBarn.tenant;
  const mappedEventTemplates = groupBy(eventTemplates, 'id')
  const mappedStatuses = groupBy(statuses, 'id')
  // We first need all barn horses since we need their pingum tag IDs to query schedules and events
  // Only grab horese if the board search key has changed
  let promise = null;
  if (filteredBarnHorses && filteredBarnHorses.length > 0) {
    promise = Promise.resolve(filteredBarnHorses);
  } else {
    promise = (boardSearchKey !== searchKey || forceBarnHorseRefresh)
      ? getAllPagedBarnHorses(
        location.barn.id,
        0,
        100,
        '&sort=horse.name,asc',
        '',
        startDate.toISOString(),
        endDate.toISOString(),
        location.id,
      )
      : Promise.resolve(barnHorses); // dates and location didn't change, keep existing horses and save an API lookup
  }

  dispatch({
    type: types.GET_BOARD_REQUESTED,
    requestId,
    dataScopeKey,
    boardBarnId: selectedBarn.id,
    boardTenant: selectedBarn.tenant,
    boardFilters,
    barnUsersMap,
    background: background && boardDataScopeKey === dataScopeKey && boardLocation?.id === location.id,
    startDate,
    endDate,
  });

  return promise.then((response) => {
    if (!isCurrentRequest()) return undefined;
    const filteredBarnHorsesForSelectedDay
      = response.filter((bh) => (moment(startDate || new Date()).isBetween(moment(bh.startDate), moment(bh.endDate || new Date(17375544137520)), null, '[)'))
        || moment(endDate || new Date()).isBetween(moment(bh.startDate), moment(bh.endDate || new Date(17375544137520)), null, '[]'));
    // don't include location in search if we are searching on subset of horses
    const tagConditions = getTagConditions(filteredBarnHorsesForSelectedDay, filteredBarnHorses && filteredBarnHorses.length > 0
      ? null
      : location, taskTypeTags, filterTags)

    const searchRequest = {
      startDate,
      endDate,
      includeArchived: false,
      tagConditions,
      eventTemplateIds,
      // assignmentAuth0UserId: auth0UserId, // TODO need to fix this at API level
      scheduleAssignmentAuth0UserId,
      auth0UserId,
    };

    // Schedules and schedule exception to get events for date range
    const scheduleSearchRequest = {
      ...searchRequest,
      auth0UserId: scheduleAssignmentAuth0UserId || auth0UserId,
    };
    delete scheduleSearchRequest.scheduleAssignmentAuth0UserId;

    const schedules = getAllPagedSearchSchedulesData(scheduleSearchRequest, 0, 100, '')

    // Get tasks (pingum events) from today and incomplete previous dates
    searchRequest.includeComplete = true;

    const tasks = getAllPagedTasks(searchRequest, 0, 100, true);
    const incompleteTasks = includeIncompleteTasks
      ? getAllPagedTasks({
        ...searchRequest,
        startDate: null,
        endDate: moment(startDate).add(-1, 'milliseconds').toDate(), // don't be inclusive of tasks starting midnight
        presetDateFilter: null,
        includeArchived: false,
        includeComplete: false,
      }, 0, 100, true)
      : Promise.resolve([]);

    const boardRequest = Promise.all([tasks, incompleteTasks, schedules]).then((values) => {
      if (!isCurrentRequest()) return;
      const updatedSchedules = values[2].map((s) => ({
        ...s,
        eventTemplate: mappedEventTemplates[s.eventTemplate.id] // elasticsearch doesn't store all event template data on schedule, add it here
          ? mappedEventTemplates[s.eventTemplate.id][0]
          : {
            ...s.eventTemplate,
            children: [],
          },
      }))
      const scheduleExceptions = updatedSchedules.map((s) => s.scheduleExceptions || []).flat();
      // once we have all data we can dispatch it
      dispatch({
        type: types.GET_BOARD_SUCCESS,
        requestId,
        boardBarnId: selectedBarn.id,
        boardTenant: selectedBarn.tenant,
        boardFilters,
        barnUsersMap,
        barnMetadata: selectedBarn.barnMetadata,
        mappedStatuses,
        tagConditions,
        barnHorses: (!location || !location.barn)
          ? allBarnHorses
          : filteredBarnHorsesForSelectedDay,
        tasks: values[0],
        incompleteTasks: values[1],
        events: generateEventsFromSchedules(
          updatedSchedules,
          scheduleExceptions,
          startDate,
          endDate,
          barnUsersMap,
          selectedBarn.barnMetadata,
          mappedStatuses,
        ),
        schedules: updatedSchedules,
        scheduleExceptions,
        boardSearchKey: searchKey,
        dataScopeKey,
        location,
      });
      const currentBoard = getState().board;
      const needsCatchUp = Object.keys(currentBoard.boardPendingChanges || {}).length > 0
        || (currentBoard.boardPendingCreations || []).length > 0;
      const retryDelays = [500, 1500, 3000];
      if (needsCatchUp && catchUpAttempt < retryDelays.length) {
        // A committed write may not be searchable yet. Keep the visible board while the index catches up.
        setTimeout(() => {
          if (!isCurrentRequest()) return;
          dispatch(getBarnBoard(startDate, endDate, location, filteredBarnHorses, taskTypeTags, filterTags, {
            includeIncompleteTasks,
            forceBarnHorseRefresh: false,
            auth0UserId,
            scheduleAssignmentAuth0UserId,
            eventTemplateIds,
            background: true,
            catchUpAttempt: catchUpAttempt + 1,
          }));
        }, retryDelays[catchUpAttempt]);
      }
    });

    if (location) {
      reconnectAttempt = 0;
      if (eventSource === null || currentSseTenant !== location.barn.tenant) {
        // eslint-disable-next-line no-use-before-define
        getServerSentEvents(dispatch, {
          startDate,
          endDate,
          tenant: location.barn.tenant,
          barnUsersMap,
          barnMetadata: selectedBarn.barnMetadata,
          mappedEventTemplates,
          mappedStatuses,
          callback: () => {
            dispatch(
              startDate,
              endDate,
              location,
              filteredBarnHorses,
              taskTypeTags,
              filterTags,
              { includeIncompleteTasks },
            )
          },
        });
      }
    }
    return boardRequest;
  }).catch((error) => {
    if (!isCurrentRequest()) return;
    console.error(error);
    dispatch({
      type: types.GET_BOARD_ERROR,
      requestId,
      message: 'There was an error getting horses for this barn and location. Please contact Questri support.',
    });
  });
};

/*
  Pingum server sent events
*/

const getServerSentEvents = (dispatch, {
  startDate,
  endDate,
  tenant,
  barnUsersMap,
  barnMetadata,
  mappedEventTemplates,
  mappedStatuses,
  callback,
}) => {
  currentSseTenant = tenant;
  if (eventSource != null) {
    eventSource.close();
    eventSource = null;
  }

  eventSource = new EventSourcePolyfill(`${env.REACT_APP_EVENT_API}/events/notifications`, {
    headers: {
      Authorization: `Bearer ${AuthService.getToken()}`,
      'X-Tenant': tenant,
    },
  });

  eventSource.addEventListener('open', () => {
    // if we reconnected right away we don't need to re grab the data
    if (reconnectAttempt > 1) {
      // TODO what do we do here??
    }
    reconnectAttempt = 0;
  });

  eventSource.addEventListener('message', (event) => {
    if (event.data && isStringJson(event.data)) {
      const parsedJson = JSON.parse(event.data);

      /*
        EVENT/TASK Update
      */
      if (parsedJson.event && isStringJson(parsedJson.event)) {
        parsedJson.event = JSON.parse(parsedJson.event);
        // let's make sure we have a current event status that is up to date
        const updatedEvent = parsedJson.event;
        // most recent event status
        const mostRecentEventStatusInOrder
          = (updatedEvent.eventStatus || []).sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());
        if (mostRecentEventStatusInOrder.length > 0) {
          // always reset the current event status from the one in the list...
          updatedEvent.currentEventStatus = mostRecentEventStatusInOrder[0];
        }
        dispatch({
          type: types.BOARD_TASK_UPDATE,
          boardTenant: tenant,
          task: {
            ...parsedJson,
            event: updatedEvent,
          },
        });
      } else if (parsedJson.scheduleException && isStringJson(parsedJson.scheduleException) && isStringJson(parsedJson.schedule)) {
        parsedJson.scheduleException = JSON.parse(parsedJson.scheduleException);
        const schedule = JSON.parse(parsedJson.schedule);
        dispatch({
          type: `BOARD_${parsedJson.eventType}_UPDATE`,
          boardTenant: tenant,
          scheduleException: {
            ...parsedJson.scheduleException,
            schedule: { id: schedule.id },
          },
          barnUsersMap,
          barnMetadata,
          mappedStatuses,
        });
      } else if (parsedJson.schedule && isStringJson(parsedJson.schedule)) {
        parsedJson.schedule = JSON.parse(parsedJson.schedule);
        dispatch({
          type: `BOARD_${parsedJson.eventType}_UPDATE`,
          boardTenant: tenant,
          schedule: {
            ...parsedJson.schedule,
            eventTemplate: mappedEventTemplates[parsedJson.schedule.eventTemplate.id]
              ? mappedEventTemplates[parsedJson.schedule.eventTemplate.id][0]
              : {
                ...parsedJson.schedule.eventTemplate,
                children: [],
              },
          },
          barnUsersMap,
          barnMetadata,
          mappedStatuses,
        });
      }
    }
  });

  eventSource.addEventListener('error', () => {
    // close out the last connection
    eventSource.close();
    eventSource = null;

    // dispatch the connection error
    dispatch({ type: types.BOARD_TASK_UPDATE_ERROR });

    // let's try to reconnect
    if (reconnectAttempt < 6) {
      setTimeout(() => {
        reconnectAttempt += 1;
        getServerSentEvents(dispatch, {
          startDate,
          endDate,
          tenant,
          barnUsersMap,
          barnMetadata,
          mappedEventTemplates,
          mappedStatuses,
          callback,
        });
      }, reconnectAttempt === 0
        ? 100
        : 30000);
      // if this is first reconnect try, attempt right away, could be blue green deployment
    } else {
      // ok reconnect didn't work, let's wait 15 minutes and do a full page refresh
      setTimeout(() => {
        callback()
      }, 900000);
    }
  });
};
